/**
 * AI Tracker 페이지
 *
 * 학생별 개념(Math Concept)별 난이도별 정답률을 시각화하는 페이지
 *
 * **매니저 전용이 아니다.** 이 경로(`/ai-tracker`)에는 App.js 의 유형 게이트가 걸려 있지 않고,
 * 데이터를 주는 genetics 3종도 서버가 `user_required` 라 교사 토큰이 통과한다
 * (BE-20260910 회신 §4-2 — 「셋 모두 이미 user_required 였다」·§5 「Manager 권한 단정 제거」).
 * 종전 주석은 「Manager 권한만 접근 가능」이었고, 그 단정을 근거로 403 문구가 등급을 지목하고 있었다.
 *
 * ========================================
 * API Endpoint
 * ========================================
 * GET /api/genetics/students?student_id={student_id}&start_date={start_date}&end_date={end_date}
 *
 * - `user_required` — 매니저·교사 통과(회신 §4-2). 어느 학생까지 보이는지는 회신에 적혀 있지 않다
 * - Request params:
 *   - (필수) student_id: 학생 ID
 *   - (선택) start_date: 시작일 (yyyy-mm-dd, KST 기준, updated_at 검색)
 *   - (선택) end_date: 종료일 (yyyy-mm-dd, KST 기준, updated_at 검색)
 *   - start_date, end_date 둘 다 없을 경우: 전체 구간 검색
 *   - 둘 중 하나만 있을 경우도 검색 가능
 * - Error codes: 400, 401, 403, 404, 500
 * - 400 Error cases:
 *   - 유효하지 않은 날짜 형식: yyyy-mm-dd 필요
 *   - 유효하지 않은 날짜: ex. 2025-02-30
 *   - 유효하지 않은 구간: end_date가 start_date보다 앞선 날짜일 경우
 *   - 유효하지 않은 범위: 1900년부터 2100년 사이의 날짜 필요
 * 
 * ========================================
 * Response 데이터 구조
 * ========================================
 * {
 *   "status": "success",
 *   "message": "Genetics students",
 *   "status_code": 200,
 *   "data": {
 *     "concepts": {
 *       "{concept_id}": [
 *         [total_correct, total_incorrect],    // index 0: 전체
 *         [d1_correct, d1_incorrect],          // index 1: 난이도 1
 *         [d2_correct, d2_incorrect],          // index 2: 난이도 2
 *         [d3_correct, d3_incorrect],          // index 3: 난이도 3
 *         [d4_correct, d4_incorrect],          // index 4: 난이도 4
 *         [d5_correct, d5_incorrect]           // index 5: 난이도 5
 *       ],
 *       ...
 *     },
 *     "solved_problems": 589,    // 전체 푼 문제 수 (중복 포함)
 *     "total_problems": 295,     // 전체 푼 문제 수 (고유)
 *     "stamps": [
 *       {
 *         "stamp_id": 64,
 *         "tree_name": "중2-1(2022)",
 *         "edu_ver": "중학교 2학년 1학기",
 *         "exams": [
 *           {
 *             "exam_id": 497,
 *             "exam_type": "alpha",
 *             "difficulty": 105,
 *             "exam_round": 4,
 *             "created_at": "2025-08-17T10:55:57.787037",
 *             "updated_at": "2025-08-17T10:56:08.201042",
 *             "length": 20,
 *             "pids_solved": {
 *               "{problem_id}": [
 *                 difficulty,  // 난이도 (tree 기준)
 *                 is_correct,  // true/false
 *                 [cid1, cid2] // 관련 concept_id 배열
 *               ],
 *               ...
 *             }
 *           },
 *           ...
 *         ]
 *       },
 *       ...
 *     ]
 *   }
 * }
 * 
 * ========================================
 * concepts 데이터 상세
 * ========================================
 * - Key: concept_id (문자열)
 * - Value: 6개 요소의 배열
 *   - [0]: [correct, incorrect] - 전체 통계
 *   - [1]: [correct, incorrect] - 난이도 1
 *   - [2]: [correct, incorrect] - 난이도 2
 *   - [3]: [correct, incorrect] - 난이도 3
 *   - [4]: [correct, incorrect] - 난이도 4
 *   - [5]: [correct, incorrect] - 난이도 5
 * 
 * 예시:
 * "375": [[7, 0], [0, 0], [3, 0], [1, 0], [3, 0], [0, 0]]
 * → concept_id 375: 전체 7맞음/0틀림, 난이도1 0/0, 난이도2 3/0, ...
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MathName from '../components/SyllabusV2/MathName';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BypassLayout } from '../components/common';
import { fetchMathConcepts, getConceptText } from '../utils/mathConceptsUtils';
// 403 문구는 세 자리(이 페이지 · AITrackerContent 의 genetics 둘)가 한 자리에서 만든다 —
// 이 경로들은 공용 apiUtils 를 지나지 않는 raw fetch 라 기존 공통 자리가 없었다
import forbiddenText from '../utils/forbiddenText';
import { Search, BookOpen, Target, AlertCircle, ToggleLeft, ToggleRight, ChevronDown, Check, ArrowUpDown, ArrowUp, ArrowDown, Calendar, RotateCcw } from 'lucide-react';

// concept_type에 따른 색상 매핑 (무지개색 50% 투명도)
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
    'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
    'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
    'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
    'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
    'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
    'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
    'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)'; // 기본 회색
};

// math_type에 따른 색상 매핑 (다른 색조로 구분)
const getMathTypeColor = (type) => {
  const colors = {
    'AL': 'rgba(99, 102, 241, 0.5)',    // 인디고 (대수학)
    'AN': 'rgba(244, 63, 94, 0.5)',     // 로즈 (해석학)
    'GE': 'rgba(16, 185, 129, 0.5)',    // 에메랄드 (기하학)
    'PS': 'rgba(168, 85, 247, 0.5)',    // 퍼플 (확률과통계)
  };
  return colors[type] || 'rgba(107, 114, 128, 0.5)'; // 기본 회색
};

// concept_type 한글 이름 매핑
const getConceptTypeName = (type) => {
  const names = {
    'DEF': '정의',
    'AXM': '공리',
    'IDN': '정리',
    'THM': '이론',
    'MTH': '방법론',
    'PRP': '성질',
    'FOR': '공식',
    'NTN': '표기법'
  };
  return names[type] || type;
};

// 오답률에 따른 텍스트 색상 반환 (10% 단위로 진해짐, 100%에 가까울수록 더 진함)
const getErrorRateColor = (errorRate) => {
  const rate = parseFloat(errorRate);
  if (rate >= 90) return 'text-red-900';
  if (rate >= 80) return 'text-red-800';
  if (rate >= 70) return 'text-red-800';
  if (rate >= 60) return 'text-red-700';
  if (rate >= 50) return 'text-red-700';
  if (rate >= 40) return 'text-red-600';
  if (rate >= 30) return 'text-red-500';
  if (rate >= 20) return 'text-red-400';
  if (rate >= 10) return 'text-red-300';
  return 'text-red-300';
};

// 오답률에 따른 배경 색상 반환 (막대그래프용)
const getErrorRateBgColor = (errorRate) => {
  const rate = parseFloat(errorRate);
  if (rate >= 90) return 'bg-red-900';
  if (rate >= 80) return 'bg-red-800';
  if (rate >= 70) return 'bg-red-800';
  if (rate >= 60) return 'bg-red-700';
  if (rate >= 50) return 'bg-red-700';
  if (rate >= 40) return 'bg-red-600';
  if (rate >= 30) return 'bg-red-500';
  if (rate >= 20) return 'bg-red-400';
  if (rate >= 10) return 'bg-red-300';
  return 'bg-red-300';
};

// 전체 컬럼용 가로 막대그래프 컴포넌트 (컴포넌트 외부 정의로 리렌더링 최적화)
const HorizontalBar = ({ correct, incorrect, maxTotal }) => {
  const total = correct + incorrect;
  if (total === 0) {
    return <div className="flex items-center justify-end text-xs text-gray-400">-</div>;
  }

  const correctPercent = (correct / total) * 100;
  const incorrectPercent = (incorrect / total) * 100;
  const barWidthPercent = (total / maxTotal) * 100;
  const errorRate = ((incorrect / total) * 100).toFixed(1);

  return (
    <div className="flex items-center gap-1">
      {/* 막대그래프 영역 - flex-1로 남은 공간 모두 사용 */}
      <div className="flex-1">
        <div
          className="flex h-4 overflow-hidden rounded"
          style={{ width: `${barWidthPercent}%`, minWidth: '8px' }}
        >
          <div
            className="h-full bg-primary"
            style={{ width: `${correctPercent}%` }}
            title={`정답: ${correct}`}
          />
          <div
            className={`h-full ${getErrorRateBgColor(errorRate)}`}
            style={{ width: `${incorrectPercent}%` }}
            title={`오답: ${incorrect}`}
          />
        </div>
      </div>
      {/* 텍스트 영역 - 고정 너비 */}
      <div className="flex w-[42px] shrink-0 flex-col items-center">
        <span className="text-[10px]">
          <span className="font-bold text-red-500">{incorrect}</span>
          <span className="text-gray-400">/</span>
          <span className="font-bold text-gray-900">{total}</span>
        </span>
        <span className={`text-[9px] ${getErrorRateColor(errorRate)}`}>
          (<span className="font-bold">{errorRate}%</span>)
        </span>
      </div>
    </div>
  );
};

// 난이도별 컬럼용 세로 막대 컴포넌트 (컴포넌트 외부 정의로 리렌더링 최적화)
// 배치: 텍스트(상단) → 정답(녹색) → 오답(빨강, 최하단 라인에 붙음)
const VERTICAL_BAR_TOTAL_HEIGHT = 50; // 셀 전체 높이 (텍스트 + 막대)
const VERTICAL_BAR_MAX_HEIGHT = 32; // 막대 최대 높이
const VERTICAL_BAR_MIN_HEIGHT = 6; // 막대 최소 높이

const VerticalBar = ({ correct, incorrect, maxDifficultyTotal }) => {
  const total = correct + incorrect;

  if (total === 0) {
    return (
      <div
        className="flex w-full items-center justify-center bg-gray-100 text-[9px] text-gray-400"
        style={{ height: `${VERTICAL_BAR_TOTAL_HEIGHT}px` }}
      >
        -
      </div>
    );
  }

  const cellHeight = Math.max(VERTICAL_BAR_MIN_HEIGHT, (total / maxDifficultyTotal) * VERTICAL_BAR_MAX_HEIGHT);
  const correctHeight = (correct / total) * cellHeight;
  const incorrectHeight = (incorrect / total) * cellHeight;
  const errorRate = ((incorrect / total) * 100).toFixed(1);

  return (
    <div className="flex w-full flex-col" style={{ height: `${VERTICAL_BAR_TOTAL_HEIGHT}px` }}>
      {/* 텍스트 - 상단 */}
      <div className="flex w-full flex-col items-center pt-1 leading-none">
        <span className="text-[9px] font-semibold">
          <span className="text-primary">{correct}</span>
          <span className="text-gray-400">/</span>
          <span className="text-red-500">{incorrect}</span>
        </span>
        <span className={`text-[7px] ${getErrorRateColor(errorRate)}`}>
          (<span className="font-bold">{errorRate}%</span>)
        </span>
      </div>
      {/* 빈 공간 - 텍스트와 막대 사이 */}
      <div className="flex-1" />
      {/* 막대 그래프 - 셀 최하단에 붙음 */}
      <div className="relative w-full" style={{ height: `${cellHeight}px` }}>
        {/* 오답 (최하단, 빨강) */}
        <div
          className={`absolute bottom-0 left-0 w-full ${getErrorRateBgColor(errorRate)}`}
          style={{ height: `${incorrectHeight}px` }}
          title={`오답: ${incorrect}`}
        />
        {/* 정답 (오답 위, 녹색) */}
        <div
          className="absolute left-0 w-full bg-primary"
          style={{ bottom: `${incorrectHeight}px`, height: `${correctHeight}px` }}
          title={`정답: ${correct}`}
        />
      </div>
    </div>
  );
};

// 날짜 범위 슬라이더 컴포넌트
const DateRangeSlider = ({ minDate, maxDate, startDate, endDate, onStartDateChange, onEndDateChange, onDragEnd, onReset, isLoading }) => {
  // 드래그 중인 임시 날짜 상태 (드래그 중에만 사용)
  const [draggingStart, setDraggingStart] = useState(null);
  const [draggingEnd, setDraggingEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);

  // 날짜를 일(day) 단위 숫자로 변환
  const dateToNumber = (dateStr) => {
    if (!dateStr) return null;
    return Math.floor(new Date(dateStr).getTime() / (1000 * 60 * 60 * 24));
  };

  // 숫자를 날짜 문자열로 변환 (yyyy-mm-dd)
  const numberToDate = (num) => {
    const date = new Date(num * 1000 * 60 * 60 * 24);
    return date.toISOString().split('T')[0];
  };

  // 날짜 포맷팅 (표시용)
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  const minNum = dateToNumber(minDate);
  const maxNum = dateToNumber(maxDate);

  // 드래그 중이면 임시 값 사용, 아니면 실제 prop 값 사용
  const displayStartDate = isDragging && draggingStart !== null ? draggingStart : startDate;
  const displayEndDate = isDragging && draggingEnd !== null ? draggingEnd : endDate;

  const startNum = dateToNumber(displayStartDate) || minNum;
  const endNum = dateToNumber(displayEndDate) || maxNum;
  const totalDays = maxNum - minNum;

  // 슬라이더 위치 계산 (퍼센트)
  const startPercent = totalDays > 0 ? ((startNum - minNum) / totalDays) * 100 : 0;
  const endPercent = totalDays > 0 ? ((endNum - minNum) / totalDays) * 100 : 100;

  // 마우스 드래그 이벤트 핸들러
  const handleMouseDown = (type) => (e) => {
    e.preventDefault();

    // 드래그 시작 시 rect를 미리 캡처
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;

    setIsDragging(true);
    // 드래그 시작 시 현재 값으로 초기화
    setDraggingStart(startDate);
    setDraggingEnd(endDate);

    let lastStartDate = startDate;
    let lastEndDate = endDate;

    const handleMouseMove = (moveEvent) => {
      const percent = Math.max(0, Math.min(100, ((moveEvent.clientX - rect.left) / rect.width) * 100));
      const dayNum = Math.round(minNum + (percent / 100) * totalDays);
      const newDate = numberToDate(dayNum);

      if (type === 'start') {
        const currentEndNum = dateToNumber(lastEndDate) || maxNum;
        if (dayNum <= currentEndNum) {
          lastStartDate = newDate;
          setDraggingStart(newDate);
        }
      } else {
        const currentStartNum = dateToNumber(lastStartDate) || minNum;
        if (dayNum >= currentStartNum) {
          lastEndDate = newDate;
          setDraggingEnd(newDate);
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setIsDragging(false);

      // 드래그가 끝났을 때 최종 값 전달 및 API 호출
      if (type === 'start') {
        onStartDateChange(lastStartDate);
      } else {
        onEndDateChange(lastEndDate);
      }

      // 드래그 끝났을 때 API 호출 트리거
      if (onDragEnd) {
        onDragEnd(lastStartDate, lastEndDate);
      }

      // 임시 상태 초기화
      setDraggingStart(null);
      setDraggingEnd(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 바 자체 드래그 이벤트 핸들러 (범위 유지하면서 이동)
  const handleBarMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // 드래그 시작 시 rect를 미리 캡처
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;

    // 현재 범위 계산
    const currentStartNum = dateToNumber(startDate) || minNum;
    const currentEndNum = dateToNumber(endDate) || maxNum;
    const rangeDays = currentEndNum - currentStartNum;

    // 드래그 시작 위치
    const startX = e.clientX;
    const startPercent = ((startX - rect.left) / rect.width) * 100;
    const startDayOffset = Math.round((startPercent / 100) * totalDays);

    setIsDragging(true);
    setDraggingStart(startDate);
    setDraggingEnd(endDate);

    let lastStartDate = startDate;
    let lastEndDate = endDate;

    const handleMouseMove = (moveEvent) => {
      const currentPercent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      const currentDayOffset = Math.round((currentPercent / 100) * totalDays);
      const dayDelta = currentDayOffset - startDayOffset;

      // 새로운 시작/종료일 계산
      let newStartNum = currentStartNum + dayDelta;
      let newEndNum = currentEndNum + dayDelta;

      // 범위 제한 (전체 범위를 벗어나지 않도록)
      if (newStartNum < minNum) {
        newStartNum = minNum;
        newEndNum = minNum + rangeDays;
      }
      if (newEndNum > maxNum) {
        newEndNum = maxNum;
        newStartNum = maxNum - rangeDays;
      }

      const newStartDate = numberToDate(newStartNum);
      const newEndDate = numberToDate(newEndNum);

      lastStartDate = newStartDate;
      lastEndDate = newEndDate;
      setDraggingStart(newStartDate);
      setDraggingEnd(newEndDate);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setIsDragging(false);

      // 드래그가 끝났을 때 최종 값 전달
      onStartDateChange(lastStartDate);
      onEndDateChange(lastEndDate);

      // API 호출 트리거
      if (onDragEnd) {
        onDragEnd(lastStartDate, lastEndDate);
      }

      // 임시 상태 초기화
      setDraggingStart(null);
      setDraggingEnd(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 날짜 입력 필드 변경 핸들러
  const handleInputChange = (type, value) => {
    if (!value) {
      if (type === 'start') {
        onStartDateChange(null);
        if (onDragEnd) onDragEnd(null, endDate);
      } else {
        onEndDateChange(null);
        if (onDragEnd) onDragEnd(startDate, null);
      }
      return;
    }

    const inputNum = dateToNumber(value);
    if (type === 'start') {
      if (inputNum >= minNum && inputNum <= maxNum && (!endDate || inputNum <= dateToNumber(endDate))) {
        onStartDateChange(value);
        if (onDragEnd) onDragEnd(value, endDate);
      }
    } else {
      if (inputNum >= minNum && inputNum <= maxNum && (!startDate || inputNum >= dateToNumber(startDate))) {
        onEndDateChange(value);
        if (onDragEnd) onDragEnd(startDate, value);
      }
    }
  };

  const hasDateFilter = startDate || endDate;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-gray-700">기간 설정</span>
          {hasDateFilter && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
              필터 적용됨
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLoading && (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          )}
          {hasDateFilter && (
            <button
              onClick={onReset}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              title="전체 기간으로 초기화"
            >
              <RotateCcw className="h-3 w-3" />
              <span>초기화</span>
            </button>
          )}
        </div>
      </div>

      {/* 슬라이더 영역 */}
      <div className="mb-3">
        {/* 전체 범위 표시 - 좌우 상단 */}
        <div className="flex justify-between text-[10px] text-gray-400 px-2">
          <span>{formatDate(minDate)}</span>
          <span>{formatDate(maxDate)}</span>
        </div>
        <div ref={sliderRef} className="relative h-8 select-none w-[calc(100%-16px)] mx-auto">
          {/* 배경 트랙 */}
          <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200" />

          {/* 선택된 범위 (드래그하여 전체 이동 가능) */}
          <div
            className="absolute top-1/2 h-3 -translate-y-1/2 cursor-move rounded-full bg-primary/40 transition-colors hover:bg-primary/50"
            style={{
              left: `${startPercent}%`,
              width: `${Math.max(endPercent - startPercent, 1)}%`,
            }}
            onMouseDown={handleBarMouseDown}
            title="드래그하여 범위 이동"
          />

          {/* 시작 핸들 */}
          <div
            className="absolute top-1/2 z-10 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-primary bg-white shadow-md transition-shadow hover:shadow-lg active:cursor-grabbing"
            style={{ left: `${startPercent}%` }}
            onMouseDown={handleMouseDown('start')}
            title={`시작: ${formatDate(displayStartDate || minDate)}`}
          >
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-gray-500">
              {formatDate(displayStartDate || minDate)}
            </div>
          </div>

          {/* 종료 핸들 */}
          <div
            className="absolute top-1/2 z-10 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-red-400 bg-white shadow-md transition-shadow hover:shadow-lg active:cursor-grabbing"
            style={{ left: `${endPercent}%` }}
            onMouseDown={handleMouseDown('end')}
            title={`종료: ${formatDate(displayEndDate || maxDate)}`}
          >
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-gray-500">
              {formatDate(displayEndDate || maxDate)}
            </div>
          </div>
        </div>
      </div>

      {/* 날짜 입력 필드 */}
      <div className="mt-6 flex h-[25px] items-center gap-4">
        <div className="flex h-[25px] flex-1 items-center gap-2">
          <label className="text-xs text-gray-500">시작</label>
          <input
            type="date"
            value={startDate || ''}
            min={minDate}
            max={endDate || maxDate}
            onChange={(e) => handleInputChange('start', e.target.value)}
            className="h-[25px] flex-1 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 dark:bg-white dark:text-gray-900 dark:border-gray-300"
          />
        </div>
        <span className="text-gray-400">~</span>
        <div className="flex h-[25px] flex-1 items-center gap-2">
          <label className="text-xs text-gray-500">종료</label>
          <input
            type="date"
            value={endDate || ''}
            min={startDate || minDate}
            max={maxDate}
            onChange={(e) => handleInputChange('end', e.target.value)}
            className="h-[25px] flex-1 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 dark:bg-white dark:text-gray-900 dark:border-gray-300"
          />
        </div>
      </div>
    </div>
  );
};

function AITracker() {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [geneticsData, setGeneticsData] = useState(null);
  const [isLoadingGenetics, setIsLoadingGenetics] = useState(false);
  const [mathConcepts, setMathConcepts] = useState([]);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAllConcepts, setShowAllConcepts] = useState(false); // 모든 개념 표시 옵션
  const [showTypeFilter, setShowTypeFilter] = useState(false); // 타입 필터 드롭다운
  const [selectedTypes, setSelectedTypes] = useState([]); // 선택된 concept type들
  const [selectedMathTypes, setSelectedMathTypes] = useState([]); // 선택된 math type들
  const [sortOrder, setSortOrder] = useState('none'); // 전체 칼럼 정렬: 'none' | 'asc' | 'desc'
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // 드롭다운 위치
  const [searchDropdownPosition, setSearchDropdownPosition] = useState({ top: 0, left: 0, width: 0 }); // 검색 드롭다운 위치

  // 날짜 필터 상태
  const [startDate, setStartDate] = useState(null); // 시작일 (yyyy-mm-dd)
  const [endDate, setEndDate] = useState(null); // 종료일 (yyyy-mm-dd)
  const [dateRange, setDateRange] = useState({ min: null, max: null }); // 전체 데이터의 날짜 범위
  const [isLoadingDateFilter, setIsLoadingDateFilter] = useState(false); // 날짜 필터 로딩 상태

  const typeFilterButtonRef = useRef(null); // 필터 버튼 ref
  const searchInputRef = useRef(null); // 검색 입력창 ref
  const searchStudentsRef = useRef(null); // 검색 함수 ref (debounce 안정성 보장)
  const dateFilterTimeoutRef = useRef(null); // 날짜 필터 debounce용 ref

  // Math Type 목록 (대수학, 해석학, 기하학, 확률통계)
  const mathTypeList = [
    { code: 'AL', name: '대수학' },
    { code: 'AN', name: '해석학' },
    { code: 'GE', name: '기하학' },
    { code: 'PS', name: '확률통계' },
  ];

  // Concept Type 목록
  const conceptTypes = [
    { code: 'DEF', name: '정의' },
    { code: 'AXM', name: '공리' },
    { code: 'IDN', name: '정리' },
    { code: 'THM', name: '이론' },
    { code: 'MTH', name: '방법론' },
    { code: 'PRP', name: '성질' },
    { code: 'FOR', name: '공식' },
    { code: 'NTN', name: '표기법' },
  ];

  // Concept Type 토글
  const toggleConceptTypeFilter = (typeCode) => {
    setSelectedTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
  };

  // Math Type 토글
  const toggleMathTypeFilter = (typeCode) => {
    setSelectedMathTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
  };

  // Concept Type 전체 선택/해제
  const toggleAllConceptTypes = () => {
    if (selectedTypes.length === conceptTypes.length) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(conceptTypes.map(t => t.code));
    }
  };

  // Math Type 전체 선택/해제
  const toggleAllMathTypes = () => {
    if (selectedMathTypes.length === mathTypeList.length) {
      setSelectedMathTypes([]);
    } else {
      setSelectedMathTypes(mathTypeList.map(t => t.code));
    }
  };

  // 모든 필터 초기화
  const resetAllFilters = () => {
    setSelectedTypes([]);
    setSelectedMathTypes([]);
  };

  // 총 선택된 필터 개수
  const totalSelectedFilters = selectedTypes.length + selectedMathTypes.length;

  // 전체 칼럼 정렬 토글 (none → desc → asc → none)
  const toggleSortOrder = () => {
    setSortOrder(prev => {
      if (prev === 'none') return 'desc';
      if (prev === 'desc') return 'asc';
      return 'none';
    });
  };

  // 드롭다운 열기 (위치 계산)
  const openTypeFilter = () => {
    if (typeFilterButtonRef.current) {
      const rect = typeFilterButtonRef.current.getBoundingClientRect();
      const buttonCenter = rect.left + (rect.width / 2);
      const dropdownWidth = 320;
      setDropdownPosition({
        top: rect.bottom + 4,
        left: buttonCenter - (dropdownWidth / 2), // 버튼 가운데 기준 정렬
      });
    }
    setShowTypeFilter(true);
  };

  // 검색 드롭다운 위치 계산
  const updateSearchDropdownPosition = useCallback(() => {
    if (searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      setSearchDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // 검색 결과가 변경될 때 드롭다운 표시 및 위치 업데이트
  useEffect(() => {
    if (searchResults.length > 0) {
      updateSearchDropdownPosition();
      setShowDropdown(true);
    }
  }, [searchResults, updateSearchDropdownPosition]);

  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      try {
        const concepts = await fetchMathConcepts();
        setMathConcepts(concepts);
      } catch (err) {
        console.error('Math concepts 로드 실패:', err);
      }
    };
    loadMathConcepts();
  }, []);

  // 학생 검색 함수
  const searchStudents = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    try {
      setIsSearching(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const params = new URLSearchParams({
        page: 1,
        per_page: 20,
        student_name: query
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('학생 검색에 실패했습니다.');
      }

      const result = await response.json();
      setSearchResults(result.data || []);
      setShowDropdown(true);
    } catch (err) {
      console.error('학생 검색 오류:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [getAccessToken, navigate]);

  // searchStudents의 최신 참조를 ref에 유지 (useEffect 의존성 문제 해결)
  searchStudentsRef.current = searchStudents;

  // Debounce된 검색 (타이핑 중 불필요한 API 호출 방지)
  useEffect(() => {
    // 빈 검색어면 즉시 결과 초기화
    if (!searchQuery || searchQuery.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(() => {
      // ref를 통해 호출하여 의존성 배열에서 searchStudents 제거
      searchStudentsRef.current?.(searchQuery);
    }, 400); // 400ms 딜레이로 타이핑 완료 후 검색

    return () => clearTimeout(timer);
  }, [searchQuery]); // searchStudents 의존성 제거 - ref로 대체

  // Genetics 데이터 로드 함수 (날짜 파라미터 지원)
  const fetchGeneticsData = useCallback(async (studentId, startDateParam = null, endDateParam = null, isDateFilterUpdate = false) => {
    try {
      if (isDateFilterUpdate) {
        setIsLoadingDateFilter(true);
      } else {
        setIsLoadingGenetics(true);
      }

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      // URL 파라미터 구성
      const params = new URLSearchParams({ student_id: studentId });
      if (startDateParam) {
        params.append('start_date', startDateParam);
      }
      if (endDateParam) {
        params.append('end_date', endDateParam);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/students?${params}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || '유효하지 않은 날짜 형식입니다.');
        }
        if (response.status === 403) {
          /**
           * 종전 문구는 「접근 권한이 없습니다. Manager 권한이 필요합니다.」였다 — 2026-09-10 로
           * **거짓이 됐다.** 이 경로는 `user_required` 라 교사도 통과하므로(회신 §4-2), 403 을
           * 등급 문제로 단정하면 교사는 있지도 않은 등급을 받으러 간다.
           *
           * 그래서 **봉투를 읽는다.** 여기까지는 403 에서 응답 본문을 한 번도 열지 않아,
           * 서버가 사유(`data.reason`)를 실어 줘도 화면에는 닿지 못했다(회신 §1-3 이 못 박은
           * 「담당·공유 범위 밖」과 게이트 403 을 사유 유무로 가른다는 그 사유다).
           * 본문이 JSON 이 아니거나 비어 있으면 사유 없음으로 다룬다. `.catch(() => null)` 이
           * 없으면 그 파싱 오류가 아래 catch 로 흘러 화면에 「Unexpected end of JSON input」이
           * 뜬다 — 403 이라는 사실도, 무엇을 하면 되는지도 함께 사라진다.
           */
          const body = await response.json().catch(() => null);
          throw new Error(forbiddenText(body?.data?.reason));
        }
        throw new Error('학생 데이터를 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      return result.data;
    } catch (err) {
      console.error('Genetics 데이터 로드 오류:', err);
      setError(err.message);
      return null;
    } finally {
      if (isDateFilterUpdate) {
        setIsLoadingDateFilter(false);
      } else {
        setIsLoadingGenetics(false);
      }
    }
  }, [getAccessToken, navigate]);

  // stamps 데이터에서 날짜 범위 추출
  const extractDateRange = useCallback((data) => {
    if (!data?.stamps || data.stamps.length === 0) {
      return { min: null, max: null };
    }

    let minDate = null;
    let maxDate = null;

    data.stamps.forEach(stamp => {
      stamp.exams?.forEach(exam => {
        if (exam.updated_at) {
          const examDate = exam.updated_at.split('T')[0]; // yyyy-mm-dd 형식으로 변환
          if (!minDate || examDate < minDate) minDate = examDate;
          if (!maxDate || examDate > maxDate) maxDate = examDate;
        }
      });
    });

    return { min: minDate, max: maxDate };
  }, []);

  // 학생 선택 시 Genetics 데이터 로드
  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);
    setShowDropdown(false);
    setSearchResults([]);
    setSearchQuery(student.student_name);
    setError(null);

    // 날짜 필터 초기화
    setStartDate(null);
    setEndDate(null);

    const data = await fetchGeneticsData(student.student_id);
    if (data) {
      setGeneticsData(data);
      // 전체 데이터에서 날짜 범위 추출
      const range = extractDateRange(data);
      setDateRange(range);
    } else {
      setGeneticsData(null);
      setDateRange({ min: null, max: null });
    }
  };

  // startDate 변경 핸들러 (상태만 업데이트, API 호출 X)
  const handleStartDateChange = useCallback((newDate) => {
    setStartDate(newDate);
  }, []);

  // endDate 변경 핸들러 (상태만 업데이트, API 호출 X)
  const handleEndDateChange = useCallback((newDate) => {
    setEndDate(newDate);
  }, []);

  // 드래그 종료 또는 입력 필드 변경 시 API 호출
  const handleDragEnd = useCallback(async (newStartDate, newEndDate) => {
    if (!selectedStudent) return;

    // 이전 타이머 취소
    if (dateFilterTimeoutRef.current) {
      clearTimeout(dateFilterTimeoutRef.current);
    }

    setIsLoadingDateFilter(true);
    const data = await fetchGeneticsData(
      selectedStudent.student_id,
      newStartDate,
      newEndDate,
      true // isDateFilterUpdate
    );
    if (data) {
      setGeneticsData(data);
    }
    setIsLoadingDateFilter(false);
  }, [selectedStudent, fetchGeneticsData]);

  // 날짜 필터 초기화
  const handleDateReset = useCallback(async () => {
    setStartDate(null);
    setEndDate(null);

    if (selectedStudent) {
      setIsLoadingDateFilter(true);
      const data = await fetchGeneticsData(selectedStudent.student_id);
      if (data) {
        setGeneticsData(data);
      }
      setIsLoadingDateFilter(false);
    }
  }, [selectedStudent, fetchGeneticsData]);


  // concept_type 추출 헬퍼 함수
  const extractConceptType = (concept) => {
    if (concept?.concept_type) return concept.concept_type;
    if (concept?.text && concept.text.includes(':')) {
      const prefix = concept.text.split(':')[0];
      if (prefix.includes('-')) {
        return prefix.split('-')[1];
      }
    }
    return null;
  };

  // Concept 데이터를 테이블 형식으로 변환 (메모이제이션으로 일관성 보장)
  const tableData = useMemo(() => {
    if (!geneticsData?.concepts && !showAllConcepts) return [];

    let result = [];

    // showAllConcepts가 true면 모든 mathConcepts를 기준으로, 아니면 geneticsData.concepts만
    if (showAllConcepts && mathConcepts.length > 0) {
      result = mathConcepts.map(concept => {
        const conceptId = String(concept.id);
        const data = geneticsData?.concepts?.[conceptId] || [[0, 0], [0, 0], [0, 0], [0, 0], [0, 0], [0, 0]];
        const conceptName = concept.text || `개념 ${conceptId}`;

        return {
          conceptId,
          conceptName,
          conceptOrder: concept.order || parseInt(conceptId),
          conceptType: extractConceptType(concept),
          mathType: concept.math_type || null,
          total: data[0] || [0, 0],
          difficulty1: data[1] || [0, 0],
          difficulty2: data[2] || [0, 0],
          difficulty3: data[3] || [0, 0],
          difficulty4: data[4] || [0, 0],
          difficulty5: data[5] || [0, 0],
        };
      });
    } else if (geneticsData?.concepts) {
      const conceptEntries = Object.entries(geneticsData.concepts);

      result = conceptEntries.map(([conceptId, data]) => {
        const conceptName = getConceptText(mathConcepts, conceptId) || `개념 ${conceptId}`;
        const concept = mathConcepts.find(c => c.id === parseInt(conceptId));

        return {
          conceptId,
          conceptName,
          conceptOrder: concept?.order || parseInt(conceptId),
          conceptType: extractConceptType(concept),
          mathType: concept?.math_type || null,
          total: data[0] || [0, 0],
          difficulty1: data[1] || [0, 0],
          difficulty2: data[2] || [0, 0],
          difficulty3: data[3] || [0, 0],
          difficulty4: data[4] || [0, 0],
          difficulty5: data[5] || [0, 0],
        };
      });
    }

    // Math Type 필터 적용
    if (selectedMathTypes.length > 0) {
      result = result.filter(row => row.mathType && selectedMathTypes.includes(row.mathType));
    }

    // Concept Type 필터 적용
    if (selectedTypes.length > 0) {
      result = result.filter(row => row.conceptType && selectedTypes.includes(row.conceptType));
    }

    // 정렬 적용
    if (sortOrder === 'asc') {
      // 오름차순: 전체 문제수 적은 순
      return [...result].sort((a, b) => (a.total[0] + a.total[1]) - (b.total[0] + b.total[1]));
    } else if (sortOrder === 'desc') {
      // 내림차순: 전체 문제수 많은 순
      return [...result].sort((a, b) => (b.total[0] + b.total[1]) - (a.total[0] + a.total[1]));
    }
    // 기본: conceptOrder 순
    return [...result].sort((a, b) => a.conceptOrder - b.conceptOrder);
  }, [geneticsData, mathConcepts, showAllConcepts, selectedTypes, selectedMathTypes, sortOrder]);

  // 최대 문제수 계산 (전체 칼럼 막대그래프 스케일링용) - tableData에 의존
  const maxTotal = useMemo(() => {
    if (tableData.length === 0) return 1;
    return Math.max(...tableData.map(row => row.total[0] + row.total[1]), 1);
  }, [tableData]);

  // 난이도 셀 중 최대 문제수 계산 - tableData에 의존
  const maxDifficultyTotal = useMemo(() => {
    if (tableData.length === 0) return 1;

    let max = 1;
    tableData.forEach(row => {
      max = Math.max(max,
        row.difficulty1[0] + row.difficulty1[1],
        row.difficulty2[0] + row.difficulty2[1],
        row.difficulty3[0] + row.difficulty3[1],
        row.difficulty4[0] + row.difficulty4[1],
        row.difficulty5[0] + row.difficulty5[1]
      );
    });
    return max;
  }, [tableData]);

  // 각 열의 합산 오답률 계산 (필터 적용된 tableData 기준)
  const errorRates = useMemo(() => {
    if (tableData.length === 0) {
      return { total: null, d1: null, d2: null, d3: null, d4: null, d5: null };
    }

    // 각 열별 정답/오답 합계 계산
    let totalCorrect = 0, totalIncorrect = 0;
    let d1Correct = 0, d1Incorrect = 0;
    let d2Correct = 0, d2Incorrect = 0;
    let d3Correct = 0, d3Incorrect = 0;
    let d4Correct = 0, d4Incorrect = 0;
    let d5Correct = 0, d5Incorrect = 0;

    tableData.forEach(row => {
      totalCorrect += row.total[0];
      totalIncorrect += row.total[1];
      d1Correct += row.difficulty1[0];
      d1Incorrect += row.difficulty1[1];
      d2Correct += row.difficulty2[0];
      d2Incorrect += row.difficulty2[1];
      d3Correct += row.difficulty3[0];
      d3Incorrect += row.difficulty3[1];
      d4Correct += row.difficulty4[0];
      d4Incorrect += row.difficulty4[1];
      d5Correct += row.difficulty5[0];
      d5Incorrect += row.difficulty5[1];
    });

    // 오답률 계산 (문제가 없으면 null)
    const calcRate = (correct, incorrect) => {
      const total = correct + incorrect;
      if (total === 0) return null;
      return ((incorrect / total) * 100).toFixed(1);
    };

    return {
      total: { rate: calcRate(totalCorrect, totalIncorrect), incorrect: totalIncorrect, count: totalCorrect + totalIncorrect },
      d1: { rate: calcRate(d1Correct, d1Incorrect), incorrect: d1Incorrect, count: d1Correct + d1Incorrect },
      d2: { rate: calcRate(d2Correct, d2Incorrect), incorrect: d2Incorrect, count: d2Correct + d2Incorrect },
      d3: { rate: calcRate(d3Correct, d3Incorrect), incorrect: d3Incorrect, count: d3Correct + d3Incorrect },
      d4: { rate: calcRate(d4Correct, d4Incorrect), incorrect: d4Incorrect, count: d4Correct + d4Incorrect },
      d5: { rate: calcRate(d5Correct, d5Incorrect), incorrect: d5Incorrect, count: d5Correct + d5Incorrect },
    };
  }, [tableData]);

  return (
    <BypassLayout
      loading={false}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1' : 'h-[calc(100vh-6rem)] w-full'
          }`}
          style={{
            ...getSafeAreaStyle(),
            width: isMobileView ? 'calc(100% - 28.8px)' : '100%',
            height: isMobileView ? 'calc(100% - 28.8px)' : '100%',
            margin: isMobileView ? '14.4px' : '0'
          }}
        >
          {/* 헤더 */}
          <div className="shrink-0">
            <div className="mb-4 flex items-center gap-3">
              <Target className="h-6 w-6 text-primary" />
              <h1 className="text-xl font-bold text-gray-800">AI Tracker</h1>
              {/* 2026-09-10 — 「Manager Only」였다. 백엔드가 교사 토큰을 열면서(회신 §4-2 — 이 화면이
                  치는 genetics 경로는 셋 다 `user_required` 다) 그 말이 거짓이 됐다. 라우트에도
                  유형 가드가 없어(App.js 의 /ai-tracker) 교사가 실제로 들어온다.
                  「스태프 전용」으로 적는다 — 학생·학부모 계정은 여전히 들어오지 못하므로 참이고,
                  매니저만이라는 단정은 없앤다. 교사에게 어느 학생까지 보이는지는 회신에 답이 없다. */}
              <span className="rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-700">
                스태프 전용
              </span>
            </div>

            {/* 검색 영역 */}
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="학생 이름으로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  // 포커스 시 검색어 초기화하여 새로 검색하도록 함
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowDropdown(false);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-4 pr-4 text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:bg-white dark:border-gray-300 dark:text-gray-900"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                </div>
              )}
            </div>

            {/* 검색 결과 드롭다운 - Portal로 body에 렌더링 */}
            {showDropdown && searchResults.length > 0 && createPortal(
              <>
                {/* 배경 클릭 시 닫기 */}
                <div
                  className="fixed inset-0"
                  onClick={() => setShowDropdown(false)}
                />
                {/* 드롭다운 메뉴 */}
                <div
                  className="fixed max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                  style={{
                    top: `${searchDropdownPosition.top}px`,
                    left: `${searchDropdownPosition.left}px`,
                    width: `${searchDropdownPosition.width}px`,
                  }}
                >
                  {searchResults.map((student) => (
                    <button
                      key={student.student_id}
                      onClick={() => handleSelectStudent(student)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
                    >
                      {/* 학생 번호 — 학생관리 표와 같은 동그란 초록 번호(오너 지시 2026-09-11) */}
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white" title={`학생 ID: ${student.student_id}`}>
                        {student.student_id}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900">{student.student_name}</p>
                        <p className="truncate text-xs text-gray-500">{student.school_name} • {student.school_grade}학년</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>,
              document.body
            )}

            {/* 선택된 학생 정보 */}
            {selectedStudent && (
              <div className="mt-4 flex items-center gap-4 rounded-lg bg-gray-50 p-3">
                {/* 학생 번호 — 학생관리 표와 같은 동그란 초록 번호(오너 지시 2026-09-11) */}
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white" title={`학생 ID: ${selectedStudent.student_id}`}>
                  {selectedStudent.student_id}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{selectedStudent.student_name}</p>
                  <p className="text-sm text-gray-500">{selectedStudent.school_name} • {selectedStudent.school_grade}학년</p>
                </div>
                {geneticsData && (
                  <div className="flex gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">총 풀이</p>
                      <p className="font-bold text-primary">{geneticsData.solved_problems}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">고유 문제</p>
                      <p className="font-bold text-green-600">{geneticsData.total_problems}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 컨텐츠 영역 */}
          <div className="flex min-h-0 flex-1 flex-col">
            {error && (
              <div className="mb-4 flex shrink-0 items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </div>
            )}

            {isLoadingGenetics ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                  <p className="mt-4 text-gray-600">데이터 로딩 중...</p>
                </div>
              </div>
            ) : geneticsData ? (
              <div className="flex min-h-0 flex-1 flex-col">
                {/* 날짜 범위 선택 슬라이더 */}
                {dateRange.min && dateRange.max && (
                  <div className="mt-4 shrink-0">
                    <DateRangeSlider
                      minDate={dateRange.min}
                      maxDate={dateRange.max}
                      startDate={startDate}
                      endDate={endDate}
                      onStartDateChange={handleStartDateChange}
                      onEndDateChange={handleEndDateChange}
                      onDragEnd={handleDragEnd}
                      onReset={handleDateReset}
                      isLoading={isLoadingDateFilter}
                    />
                  </div>
                )}

                {/* 범례 및 옵션 - 고정 영역 */}
                <div className="mt-2 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 py-1 px-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded bg-primary"></div>
                      <span className="text-xs">정답</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-3 w-3 rounded bg-red-500"></div>
                      <span className="text-xs">오답</span>
                    </div>
                  </div>

                  {/* 모든 개념 표시 토글 */}
                  <button
                    onClick={() => setShowAllConcepts(!showAllConcepts)}
                    className="flex items-center gap-1.5 rounded-3xl border border-gray-300 bg-white px-3 py-0.5 text-xs transition-colors hover:bg-gray-100"
                  >
                    {showAllConcepts ? (
                      <ToggleRight className="h-3 w-3 text-primary" />
                    ) : (
                      <ToggleLeft className="h-3 w-3 text-gray-400" />
                    )}
                    <span className={showAllConcepts ? 'text-primary font-medium' : 'text-gray-600'}>
                      모든 개념
                    </span>
                  </button>
                </div>

                {/* 테이블 영역 - 스크롤 (오버레이 스크롤바) */}
                <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200 scrollbar-overlay">
                  <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-100">
                      <th className="sticky left-0 top-0 z-20 w-[30%] min-w-[180px] border border-gray-200 bg-gray-100 px-3 py-1 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex items-center justify-between gap-2">
                          <span>
                            Math Concept
                            <span className="ml-1 text-xs font-normal text-gray-500">
                              ({tableData.length}개)
                            </span>
                          </span>

                          {/* Type 필터 버튼 */}
                          <button
                            ref={typeFilterButtonRef}
                            onClick={openTypeFilter}
                            className={`flex items-center gap-1 rounded-3xl px-2 py-1 text-xs transition-colors ${
                              totalSelectedFilters > 0
                                ? 'bg-primary/20 text-primary'
                                : 'bg-white text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <span>Type</span>
                            {totalSelectedFilters > 0 && (
                              <span className="rounded-full bg-primary px-1.5 text-white">
                                {totalSelectedFilters}
                              </span>
                            )}
                            <ChevronDown className={`h-3 w-3 transition-transform ${showTypeFilter ? 'rotate-180' : ''}`} />
                          </button>

                          {/* 드롭다운 메뉴 - Portal로 body에 렌더링 */}
                          {showTypeFilter && createPortal(
                            <>
                              {/* 배경 클릭 시 닫기 */}
                              <div
                                className="fixed inset-0"
                                onClick={() => setShowTypeFilter(false)}
                              />
                              {/* 드롭다운 메뉴 - 2열 레이아웃 */}
                              <div
                                className="fixed rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
                                style={{
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                  width: '320px',
                                }}
                              >
                                <div className="flex">
                                  {/* 좌측: Math Type 칼럼 */}
                                  <div className="w-1/2 border-r border-gray-100 px-2">
                                    <div className="mb-1 flex items-center justify-between px-1">
                                      <span className="text-[10px] font-semibold text-gray-500">Math Type</span>
                                      <button
                                        onClick={toggleAllMathTypes}
                                        className="text-[9px] text-primary hover:underline"
                                      >
                                        {selectedMathTypes.length === mathTypeList.length ? '해제' : '전체'}
                                      </button>
                                    </div>
                                    {mathTypeList.map(type => (
                                      <button
                                        key={type.code}
                                        onClick={() => toggleMathTypeFilter(type.code)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50"
                                      >
                                        <span
                                          className="inline-flex h-4 w-8 items-center justify-center rounded text-[9px] font-semibold"
                                          style={{ backgroundColor: getMathTypeColor(type.code) }}
                                        >
                                          {type.code}
                                        </span>
                                        <span className="flex-1 text-left">{type.name}</span>
                                        {selectedMathTypes.includes(type.code) && (
                                          <Check className="h-3 w-3 text-primary" />
                                        )}
                                      </button>
                                    ))}
                                  </div>

                                  {/* 우측: Concept Type 칼럼 */}
                                  <div className="w-1/2 px-2">
                                    <div className="mb-1 flex items-center justify-between px-1">
                                      <span className="text-[10px] font-semibold text-gray-500">Concept Type</span>
                                      <button
                                        onClick={toggleAllConceptTypes}
                                        className="text-[9px] text-primary hover:underline"
                                      >
                                        {selectedTypes.length === conceptTypes.length ? '해제' : '전체'}
                                      </button>
                                    </div>
                                    {conceptTypes.map(type => (
                                      <button
                                        key={type.code}
                                        onClick={() => toggleConceptTypeFilter(type.code)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50"
                                      >
                                        <span
                                          className="inline-flex h-4 w-8 items-center justify-center rounded text-[9px] font-semibold"
                                          style={{ backgroundColor: getConceptTypeColor(type.code) }}
                                        >
                                          {type.code}
                                        </span>
                                        <span className="flex-1 text-left">{type.name}</span>
                                        {selectedTypes.includes(type.code) && (
                                          <Check className="h-3 w-3 text-primary" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* 필터 초기화 */}
                                {totalSelectedFilters > 0 && (
                                  <>
                                    <div className="mx-2 my-2 border-t border-gray-100" />
                                    <button
                                      onClick={resetAllFilters}
                                      className="w-full px-3 py-1.5 text-xs text-red-500 hover:bg-red-50"
                                    >
                                      필터 초기화
                                    </button>
                                  </>
                                )}
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                      </th>
                      <th
                        className="sticky top-0 min-w-[120px] cursor-pointer border border-gray-200 bg-gray-100 px-3 py-1 text-center text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 whitespace-nowrap"
                        onClick={toggleSortOrder}
                        title={sortOrder === 'none' ? '클릭하여 정렬' : sortOrder === 'asc' ? '오름차순' : '내림차순'}
                      >
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <div className="flex items-center justify-center gap-1">
                            <span>Total</span>
                            {sortOrder === 'none' && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                            {sortOrder === 'asc' && <ArrowUp className="h-3 w-3 text-primary" />}
                            {sortOrder === 'desc' && <ArrowDown className="h-3 w-3 text-primary" />}
                          </div>
                          {errorRates.total.rate !== null && (
                            <>
                              <span className="text-[9px]">
                                <span className="font-bold text-red-500">{errorRates.total.incorrect}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-bold text-gray-900">{errorRates.total.count}</span>
                              </span>
                              <span className={`text-[9px] font-normal ${getErrorRateColor(errorRates.total.rate)}`}>
                                (<span className="font-bold">{errorRates.total.rate}%</span>)
                              </span>
                            </>
                          )}
                        </div>
                      </th>
                      <th className="sticky top-0 min-w-[30px] border border-gray-200 bg-gray-100 px-1 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <span>Lv1</span>
                          {errorRates.d1.rate !== null && (
                            <>
                              <span className="text-[9px]">
                                <span className="font-bold text-red-500">{errorRates.d1.incorrect}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-bold text-gray-900">{errorRates.d1.count}</span>
                              </span>
                              <span className={`text-[9px] font-normal ${getErrorRateColor(errorRates.d1.rate)}`}>(<span className="font-bold">{errorRates.d1.rate}%</span>)</span>
                            </>
                          )}
                        </div>
                      </th>
                      <th className="sticky top-0 min-w-[30px] border border-gray-200 bg-gray-100 px-1 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <span>Lv2</span>
                          {errorRates.d2.rate !== null && (
                            <>
                              <span className="text-[9px]">
                                <span className="font-bold text-red-500">{errorRates.d2.incorrect}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-bold text-gray-900">{errorRates.d2.count}</span>
                              </span>
                              <span className={`text-[9px] font-normal ${getErrorRateColor(errorRates.d2.rate)}`}>(<span className="font-bold">{errorRates.d2.rate}%</span>)</span>
                            </>
                          )}
                        </div>
                      </th>
                      <th className="sticky top-0 min-w-[30px] border border-gray-200 bg-gray-100 px-1 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <span>Lv3</span>
                          {errorRates.d3.rate !== null && (
                            <>
                              <span className="text-[9px]">
                                <span className="font-bold text-red-500">{errorRates.d3.incorrect}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-bold text-gray-900">{errorRates.d3.count}</span>
                              </span>
                              <span className={`text-[9px] font-normal ${getErrorRateColor(errorRates.d3.rate)}`}>(<span className="font-bold">{errorRates.d3.rate}%</span>)</span>
                            </>
                          )}
                        </div>
                      </th>
                      <th className="sticky top-0 min-w-[30px] border border-gray-200 bg-gray-100 px-1 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <span>Lv4</span>
                          {errorRates.d4.rate !== null && (
                            <>
                              <span className="text-[9px]">
                                <span className="font-bold text-red-500">{errorRates.d4.incorrect}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-bold text-gray-900">{errorRates.d4.count}</span>
                              </span>
                              <span className={`text-[9px] font-normal ${getErrorRateColor(errorRates.d4.rate)}`}>(<span className="font-bold">{errorRates.d4.rate}%</span>)</span>
                            </>
                          )}
                        </div>
                      </th>
                      <th className="sticky top-0 min-w-[30px] border border-gray-200 bg-gray-100 px-1 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <span>Lv5</span>
                          {errorRates.d5.rate !== null && (
                            <>
                              <span className="text-[9px]">
                                <span className="font-bold text-red-500">{errorRates.d5.incorrect}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-bold text-gray-900">{errorRates.d5.count}</span>
                              </span>
                              <span className={`text-[9px] font-normal ${getErrorRateColor(errorRates.d5.rate)}`}>(<span className="font-bold">{errorRates.d5.rate}%</span>)</span>
                            </>
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row) => {
                        // concept_type 추출 (text에 포함된 경우 대비)
                        const conceptType = row.conceptType ||
                          (row.conceptName && row.conceptName.includes(':') ?
                            row.conceptName.split(':')[0].split('-')[1] : null);

                        // 개념 이름에서 타입 제거
                        const displayName = row.conceptName.includes(':') ?
                          row.conceptName.split(':').slice(1).join(':').trim() :
                          row.conceptName;

                        return (
                          <tr key={row.conceptId} className="hover:bg-gray-50/50">
                            {/* Math Concept 칼럼 */}
                            <td className="sticky left-0 z-10 w-[30%] min-w-[180px] border border-gray-200 bg-white px-1 py-0.5 text-sm">
                              <div className="flex items-center gap-2">
                                {/* 뱃지 영역 - 세로 1열 배치 */}
                                <div className="flex shrink-0 flex-col gap-0.5">
                                  {/* Order 뱃지 */}
                                  <span
                                    className="inline-flex items-center justify-center rounded font-semibold text-gray-700"
                                    style={{
                                      backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                      width: '28px',
                                      height: '16px',
                                      fontSize: '9px'
                                    }}
                                    title={`순서: ${row.conceptOrder}`}
                                  >
                                    {row.conceptOrder}
                                  </span>

                                  {/* Math Type 뱃지 */}
                                  {row.mathType && (
                                    <span
                                      className="inline-flex items-center justify-center rounded font-semibold text-gray-800"
                                      style={{
                                        backgroundColor: getMathTypeColor(row.mathType),
                                        width: '28px',
                                        height: '16px',
                                        fontSize: '8px'
                                      }}
                                      title={`Math Type: ${row.mathType}`}
                                    >
                                      {row.mathType}
                                    </span>
                                  )}

                                  {/* Concept Type 뱃지 */}
                                  {conceptType && (
                                    <span
                                      className="inline-flex items-center justify-center rounded font-semibold text-gray-800"
                                      style={{
                                        backgroundColor: getConceptTypeColor(conceptType),
                                        width: '28px',
                                        height: '16px',
                                        fontSize: '8px'
                                      }}
                                      title={getConceptTypeName(conceptType)}
                                    >
                                      {conceptType}
                                    </span>
                                  )}
                                </div>

                                {/* 개념 이름 - 남은 영역 모두 사용 */}
                                <span className="min-w-0 flex-1 font-medium text-gray-800 line-clamp-3"><MathName text={displayName} compact /></span>
                              </div>
                            </td>

                            {/* 전체 칼럼 - 가로 막대그래프 */}
                            <td className="border border-gray-200 px-1 py-0.5">
                              <HorizontalBar correct={row.total[0]} incorrect={row.total[1]} maxTotal={maxTotal} />
                            </td>

                            {/* 난이도별 칼럼 - 세로 막대 */}
                            <td className="border border-gray-200 p-0 align-top">
                              <VerticalBar correct={row.difficulty1[0]} incorrect={row.difficulty1[1]} maxDifficultyTotal={maxDifficultyTotal} />
                            </td>
                            <td className="border border-gray-200 p-0 align-top">
                              <VerticalBar correct={row.difficulty2[0]} incorrect={row.difficulty2[1]} maxDifficultyTotal={maxDifficultyTotal} />
                            </td>
                            <td className="border border-gray-200 p-0 align-top">
                              <VerticalBar correct={row.difficulty3[0]} incorrect={row.difficulty3[1]} maxDifficultyTotal={maxDifficultyTotal} />
                            </td>
                            <td className="border border-gray-200 p-0 align-top">
                              <VerticalBar correct={row.difficulty4[0]} incorrect={row.difficulty4[1]} maxDifficultyTotal={maxDifficultyTotal} />
                            </td>
                            <td className="border border-gray-200 p-0 align-top">
                              <VerticalBar correct={row.difficulty5[0]} incorrect={row.difficulty5[1]} maxDifficultyTotal={maxDifficultyTotal} />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>

                {tableData.length === 0 && (
                  <div className="py-12 text-center text-gray-500">
                    해당 학생의 개념별 데이터가 없습니다.
                  </div>
                )}
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-gray-500">
                <BookOpen className="mb-4 h-16 w-16 text-gray-300" />
                <p className="text-lg font-medium">학생을 검색하여 선택해주세요</p>
                <p className="mt-2 text-sm">학생의 개념별 정답률을 난이도별로 확인할 수 있습니다</p>
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
}

export default AITracker;

